import { EnumButtonName } from '@calm/evexia-analytics';
import { useGuideQueue } from '@rhg/hooks/use-guide-queue';
import playerMessages from '@rhg/lang/messages/player';
import {
    Box,
    IconButton,
    IconButtonProps,
    SxProps,
    Theme,
} from '@rhg/libs/material';
import { Loop, LoopOne } from '@rhg/libs/material/icons';
import { nextLoopMode } from '@rhg/providers/guide-queue/capabilities';
import { LoopCapability, LoopMode } from '@rhg/providers/guide-queue/types';
import { useIntl } from 'react-intl';

export interface LoopButtonProps {
    sx?: SxProps<Theme>;
    size?: IconButtonProps['size'];
    variant?: IconButtonProps['variant'];
}

// Analytics `Loop` property values (parity with iOS/Android): the mode the
// click switches TO.
const LOOP_ANALYTICS_LABEL: Record<LoopMode, 'Continuous' | 'One' | 'Off'> = {
    [LoopMode.All]: 'Continuous',
    [LoopMode.One]: 'One',
    [LoopMode.Off]: 'Off',
};

const LOOP_ARIA_KEY: Record<LoopMode, keyof typeof playerMessages> = {
    [LoopMode.Off]: 'player.loop_off',
    [LoopMode.All]: 'player.loop_all',
    [LoopMode.One]: 'player.loop_one',
};

// The active indicator dot is a decorator drawn below the (square) glyph rather
// than baked into the asset — same approach as evexia-android. It shares the
// glyph's active tint and only appears when looping is on.
const styleIconWrapper = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
};

const styleActiveDot = {
    position: 'absolute',
    bottom: '-0.2em',
    width: '0.2em',
    height: '0.2em',
    borderRadius: '50%',
    bgcolor: 'info.main',
};

export function LoopButton({ sx, size, variant }: LoopButtonProps) {
    const { formatMessage } = useIntl();
    const { loopMode, loopCapability, cycleLoop } = useGuideQueue();

    if (loopCapability === LoopCapability.Unsupported) {
        return null;
    }

    const isActive = loopMode !== LoopMode.Off;
    // The Loop property logs the resulting state (the mode being switched to).
    const upcoming = nextLoopMode(loopMode, loopCapability);
    // Single-loopable content (2-state RepeatOneOnly) always uses the LoopOne
    // glyph, toggling only off↔on — parity with mobile (EVE-13642). The 3-state
    // Playlist cycle keeps the plain Loop glyph until Loop One is reached.
    const Icon =
        loopCapability === LoopCapability.RepeatOneOnly ||
        loopMode === LoopMode.One
            ? LoopOne
            : Loop;

    return (
        <IconButton
            trackButton={EnumButtonName.Loop}
            trackProps={{
                Loop: LOOP_ANALYTICS_LABEL[upcoming],
                // Override the ambient (pre-click) IsLooped so it matches the
                IsLooped: LOOP_ANALYTICS_LABEL[upcoming],
            }}
            aria-pressed={isActive}
            aria-label={formatMessage(playerMessages[LOOP_ARIA_KEY[loopMode]])}
            onClick={cycleLoop}
            size={size}
            variant={variant}
            sx={sx}
        >
            <Box sx={styleIconWrapper}>
                <Icon color={isActive ? 'info' : 'inherit'} />
                {isActive && (
                    <Box
                        data-testid="loop-dot"
                        aria-hidden="true"
                        sx={styleActiveDot}
                    />
                )}
            </Box>
        </IconButton>
    );
}
